import { LESSON_NAV_SHADOW_L1 } from '@/components/lesson/lessonNavButtonStyles';
import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';
import { Button, SizableText, XStack } from 'tamagui';

const COMPLETE_ICON_SLOT = 20;

function CompleteIconOrSpinner({
  isLoading,
  isCompleted,
  color,
}: {
  isLoading: boolean;
  isCompleted: boolean;
  color: string;
}) {
  return (
    <View
      style={{
        width: COMPLETE_ICON_SLOT,
        height: COMPLETE_ICON_SLOT,
        overflow: 'hidden',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isLoading ? (
        <ActivityIndicator
          color={color}
          {...(Platform.OS === 'android'
            ? { size: COMPLETE_ICON_SLOT }
            : { size: 'small' })}
          style={
            Platform.OS === 'android'
              ? undefined
              : {
                  transform: [{ scale: 0.68 }],
                }
          }
        />
      ) : (
        <Ionicons
          name={isCompleted ? 'checkmark-circle' : 'checkmark-circle-outline'}
          size={COMPLETE_ICON_SLOT}
          color={color}
        />
      )}
    </View>
  );
}

interface MarkCompleteButtonProps {
  isCompleted: boolean;
  isLoading: boolean;
  isFiftyPercentWatched: boolean;
  onPress: () => void;
  isMobile?: boolean;
  isActive: boolean;
  variant?: 'inline' | 'standalone' | 'toolbar';
}

export const MarkCompleteButton: React.FC<MarkCompleteButtonProps> = ({
  isCompleted,
  isLoading,
  isFiftyPercentWatched,
  onPress,
  isMobile = false,
  variant = 'inline',
  isActive = false,
}) => {
  const isEnabled = isFiftyPercentWatched || isCompleted;
  const buttonHeight = 40;
  const buttonSize = isMobile ? '$3' : '$4';

  const [toolbarHovered, setToolbarHovered] = useState(false);
  const [toolbarPressed, setToolbarPressed] = useState(false);

  const toolbarInteractiveDark =
    variant === 'toolbar' &&
    isEnabled &&
    !isCompleted &&
    (toolbarHovered || toolbarPressed);

  const toolbarInverse =
    variant === 'toolbar' &&
    (isCompleted || toolbarInteractiveDark);

  const toolbarFg = !isEnabled
    ? '#C9C5C3'
    : toolbarInverse
      ? '#FFFFFF'
      : '#000000';

  const toolbarBg = !isEnabled
    ? '#FFFFFF'
    : toolbarInverse
      ? '#0D0D0D'
      : '#FFFFFF';

  const toolbarBorderColor = toolbarInverse ? '#0D0D0D' : '#C9C5C3';

  const toolbarColors =
    variant === 'toolbar'
      ? {
          bg: toolbarBg,
          color: toolbarFg,
          borderWidth: 1 as const,
          borderColor: toolbarBorderColor,
          opacity: isEnabled ? 1 : 0.4,
        }
      : null;

  const labelText = isCompleted ? 'Unmark Complete' : 'Mark Complete';

  return (
    <View
      style={{
        height: buttonHeight,
        justifyContent: 'center',
        alignItems: 'flex-start',
        ...(isMobile && { alignSelf: 'stretch' }),
        ...(variant === 'standalone' && { marginTop: 24, marginBottom: 16 }),
      }}
    >
      {isActive && (
        <Button
          bg={
            toolbarColors
              ? toolbarColors.bg
              : isCompleted
                ? '#0D0D0D'
                : isFiftyPercentWatched
                  ? '#0D0D0D'
                  : '#A8A8A8'
          }
          color={toolbarColors ? toolbarColors.color : 'white'}
          br={variant === 'toolbar' ? 30 : 999}
          {...(variant === 'toolbar'
            ? { jc: 'center' as const, ai: 'center' as const, fd: 'row' as const }
            : {})}
          iconAfter={
            variant === 'toolbar' ? undefined : (
              <CompleteIconOrSpinner
                isLoading={isLoading}
                isCompleted={isCompleted}
                color={
                  isLoading
                    ? !isCompleted && !isFiftyPercentWatched
                      ? '#5C5854'
                      : 'white'
                    : toolbarColors
                      ? toolbarColors.color
                      : 'white'
                }
              />
            )
          }
          pressStyle={
            variant === 'toolbar'
              ? { opacity: 0.92 }
              : { opacity: 0.85 }
          }
          onHoverIn={
            variant === 'toolbar' && Platform.OS === 'web'
              ? () => setToolbarHovered(true)
              : undefined
          }
          onHoverOut={
            variant === 'toolbar' && Platform.OS === 'web'
              ? () => setToolbarHovered(false)
              : undefined
          }
          onPressIn={variant === 'toolbar' ? () => setToolbarPressed(true) : undefined}
          onPressOut={variant === 'toolbar' ? () => setToolbarPressed(false) : undefined}
          style={{
            boxShadow: variant === 'toolbar' ? LESSON_NAV_SHADOW_L1 : '0 4px 12px rgba(0,0,0,0.2)',
            cursor: 'pointer',
            height: buttonHeight,
            paddingVertical: variant === 'toolbar' ? 4 : undefined,
            paddingHorizontal: variant === 'toolbar' ? 16 : undefined,
            gap: variant === 'toolbar' ? 6 : undefined,
            ...(toolbarColors && {
              borderWidth: toolbarColors.borderWidth,
              borderColor: toolbarColors.borderColor,
            }),
            ...(isMobile && { alignSelf: 'stretch' }),
          }}
          onPress={onPress}
          disabled={isLoading}
          opacity={toolbarColors ? toolbarColors.opacity : isEnabled ? 1 : 0.65}
          size={buttonSize}
          fontWeight="600"
        >
          {variant === 'toolbar' ? (
            <XStack ai="center" gap={6}>
              <SizableText
                color={toolbarFg}
                fontSize={14}
                fontWeight="600"
                lineHeight={20}
                letterSpacing={0}
              >
                {labelText}
              </SizableText>
              <CompleteIconOrSpinner
                isLoading={isLoading}
                isCompleted={isCompleted}
                color={toolbarFg}
              />
            </XStack>
          ) : isCompleted ? (
            'Unmark Complete'
          ) : (
            'Mark Complete'
          )}
        </Button>
      )}
    </View>
  );
};
